/**
 * Página do Dashboard - Versão Minimalista com Drag & Drop
 * Visão geral com métricas e resumos personalizáveis
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  Link,
  IconButton,
  Collapse,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import './Dashboard.css';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getMetricas, getFluxoMensal } from '../../services/dashboardService';
import api from '../../services/api';
import ChatIcon from '@mui/icons-material/Chat';
import Navbar from '../../components/Navbar';
import Sidebar from '../../components/Sidebar';
import SettingsModal from '../../components/SettingsModal';
import DashboardSidebar from '../../components/DashboardSidebar';
import GraficoBarras from '../../components/GraficoBarras';
import GraficoPizza from '../../components/GraficoPizza';
import GraficoLinha from '../../components/GraficoLinha';
import GraficoRosca from '../../components/GraficoRosca';

/**
 * Componente de item de lista de dados
 */
const DataListItem = ({ label, value, showBorder = false, valueColor = 'inherit', valueWeight = 500 }) => (
  <ListItem
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1.5,
      py: 1,
      px: 0,
      fontSize: '0.95em',
      borderTop: showBorder ? '1px dotted' : 'none',
      borderColor: 'divider',
      pt: showBorder ? 1.25 : 1,
    }}
  >
    <Tooltip title={label} enterDelay={400}>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Tooltip>
    <Typography variant="body2" sx={{ fontWeight: valueWeight, color: valueColor, flexShrink: 0, whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </ListItem>
);

/**
 * Página do Dashboard
 */

const Dashboard = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metricas, setMetricas] = useState(null);
  // ...
  // Estado para lançamentos do mês
  const [lancamentos, setLancamentos] = useState([]);
  // Estado para dados do gráfico de fluxo
  const [dadosFluxo, setDadosFluxo] = useState([]);


  // Controle do menu lateral
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Controle do modal de configurações
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Configuração de layout (1, 2 ou 3 colunas)
  const [numColunas, setNumColunas] = useState(3);
  // No celular ignora a preferência de colunas e força 1 — evita cards e gráficos espremidos
  const isMobile = useMediaQuery('(max-width:768px)');
  const colunasEfetivas = isMobile ? 1 : numColunas;

  // Visibilidade dos cards
  const [cardsVisiveis, setCardsVisiveis] = useState({
  resultado: true,
  impostos: true,
  fluxo: true, // Força o card de fluxo a ficar visível para debug
  despesas: true,
  dre: true,
  receitas: true,
  lancamentos: true,
  });

  // Estado de expansão dos cards (acordeão)
  const [cardsExpandidos, setCardsExpandidos] = useState({
    dre: true,
    resultado: true,
    impostos: true,
    fluxo: true,
    despesas: true,
    receitas: true,
    lancamentos: true,
  });

  // Ordem dos cards (para drag and drop)
  const [ordemCards, setOrdemCards] = useState([
  'resultado',
  'impostos',
  'fluxo',
  'despesas',
  'dre',
  'lancamentos',
  ]);

  // Estado para drag and drop (API HTML5 nativa)
  const [draggedIndex, setDraggedIndex] = useState(null);

  const dataAtual = new Date();
  const [mesAtual, setMesAtual] = useState(dataAtual.getMonth() + 1);
  const [anoAtual, setAnoAtual] = useState(dataAtual.getFullYear());

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  /**
   * Handler para mudança de mês no sidebar
   */
  const handleMesChange = (novoMes, novoAno) => {
    setMesAtual(novoMes);
    setAnoAtual(novoAno);
  };

  /**
   * Alterna expansão/recolhimento do card
   */
  const toggleCardExpansao = (cardId) => {
    const novosCardsExpandidos = {
      ...cardsExpandidos,
      [cardId]: !cardsExpandidos[cardId]
    };
    setCardsExpandidos(novosCardsExpandidos);
    localStorage.setItem('dashboardCardsExpandidos', JSON.stringify(novosCardsExpandidos));
  };

  /**
   * Handlers para drag and drop HTML5 nativo (funciona perfeitamente em React)
   */
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };


  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newOrder = [...ordemCards];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);
    setOrdemCards(newOrder);
    setDraggedIndex(index);
    // Salva ordem no localStorage a cada alteração
    localStorage.setItem('dashboardOrdemCards', JSON.stringify(newOrder));
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    // Ordem já salva no handleDragOver
  };

  /**
   * Busca métricas do backend
   */
  const carregarMetricas = async () => {
    try {
      setLoading(true);
      setError('');
      const dados = await getMetricas(mesAtual, anoAtual);
  // ...
      setMetricas(dados);
    } catch (err) {
      console.error('❌ Erro ao carregar métricas:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Carregar preferências salvas ao montar o componente
  useEffect(() => {
    const savedNumColunas = localStorage.getItem('dashboardNumColunas');
    const savedCardsVisiveis = localStorage.getItem('dashboardCardsVisiveis');
    const savedCardsExpandidos = localStorage.getItem('dashboardCardsExpandidos');
    const savedOrdemCards = localStorage.getItem('dashboardOrdemCards');

    if (savedNumColunas) {
      setNumColunas(parseInt(savedNumColunas));
    }
    if (savedCardsVisiveis) {
      setCardsVisiveis(JSON.parse(savedCardsVisiveis));
    }
    if (savedCardsExpandidos) {
      setCardsExpandidos(JSON.parse(savedCardsExpandidos));
    }
    if (savedOrdemCards) {
      try {
        const parsed = JSON.parse(savedOrdemCards);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setOrdemCards(parsed);
        }
      } catch (e) {}
    }
  }, []);


  // Carregar métricas e lançamentos ao mudar mês/ano
  useEffect(() => {
    carregarMetricas();
    // Buscar lançamentos do mês
    const carregarLancamentos = async () => {
      try {
        const resp = await api.get(`/lancamentos-mes?mes=${mesAtual}&ano=${anoAtual}`);
        setLancamentos(resp.data || resp || []);
      } catch (err) {
        setLancamentos([]);
      }
    };
    carregarLancamentos();

    // Buscar dados reais do fluxo de caixa
    const carregarFluxo = async () => {
      try {
        const resp = await getFluxoMensal();
        // Se vier .data, usa .data, senão usa direto
  const dados = resp.data || resp;
  setDadosFluxo(dados); // Agora pode ser objeto, não força array
      } catch (err) {
        setDadosFluxo([]);
      }
    };
    carregarFluxo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesAtual, anoAtual]);

  /**
   * Formata valor em reais
   */
  const formatarMoeda = (valor) => {
    return `R$ ${parseFloat(valor || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  /**
   * Card de impostos da Pessoa Jurídica — muda de conteúdo conforme o regime
   * tributário da clínica (Simples Nacional x Lucro Presumido).
   */
  const renderCardImpostoPJ = (pj) => {
    const regime = pj?.regime || 'Simples Nacional';
    const isPresumido = regime === 'Lucro Presumido';
    return (
      <>
        <Typography variant="subtitle1" sx={{ fontSize: '0.8em', mt: 0, mb: 1.5, color: '#EA580C', fontWeight: 700, background: 'rgba(249,115,22,0.09)', px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          {regime} (PJ)
        </Typography>
        <List sx={{ p: 0 }}>
          <DataListItem
            label="Faturamento (PJ)"
            value={formatarMoeda(pj?.faturamento)}
            valueColor="#00B389"
            valueWeight={700}
          />
          {!isPresumido && (
            <DataListItem
              label="RBT12 (Receita 12 meses)"
              value={formatarMoeda(pj?.rbt12)}
            />
          )}
          <DataListItem
            label="Alíquota Efetiva"
            value={`${pj?.aliquotaEfetiva}%`}
          />
          {isPresumido && pj?.detalhamento && (
            <>
              <DataListItem label="PIS (mês)" value={formatarMoeda(pj.detalhamento.pis)} />
              <DataListItem label="COFINS (mês)" value={formatarMoeda(pj.detalhamento.cofins)} />
              <DataListItem label="ISS (mês)" value={formatarMoeda(pj.detalhamento.iss)} />
              <Box sx={{ mt: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>
                  IRPJ/CSLL são apurados por trimestre, não por mês{pj.trimestreLabel ? ` — ${pj.trimestreLabel}` : ''}
                </Typography>
              </Box>
              <DataListItem label="Faturamento do trimestre" value={formatarMoeda(pj.faturamentoTrimestral)} />
              <DataListItem label="IRPJ (trimestre)" value={formatarMoeda(pj.detalhamento.irpj)} />
              {parseFloat(pj.detalhamento.irAdicional) > 0 && (
                <DataListItem label="IRPJ Adicional (trimestre)" value={formatarMoeda(pj.detalhamento.irAdicional)} />
              )}
              <DataListItem label="CSLL (trimestre)" value={formatarMoeda(pj.detalhamento.csll)} />
            </>
          )}
          {isPresumido ? (
            <>
              <DataListItem
                label="PIS+COFINS+ISS a pagar (mês)"
                value={formatarMoeda(pj?.das)}
                valueColor="#FF4873"
                valueWeight={700}
              />
              <DataListItem
                label="IRPJ+CSLL a pagar (trimestre)"
                value={formatarMoeda(pj?.impostoRenda)}
                showBorder
                valueColor="#FF4873"
                valueWeight={700}
              />
            </>
          ) : (
            <DataListItem
              label="DAS a Pagar"
              value={formatarMoeda(pj?.das)}
              showBorder
              valueColor="#FF4873"
              valueWeight={700}
            />
          )}
        </List>
      </>
    );
  };

  /**
   * Calcula valores de receitas e despesas
   */
  const calcularValores = () => {
    if (!metricas) return { receitas: 0, despesas: 0, resultado: 0, controleInterno: 0 };

    // Controle interno entra no resultado (é dinheiro real), mas não nos impostos
    const controleInterno = parseFloat(metricas.controleInterno || 0);
    const receitas = parseFloat(metricas.pessoaFisica?.rendimentos || 0) +
      parseFloat(metricas.pessoaJuridica?.faturamento || 0) + controleInterno;
    const despesas = parseFloat(metricas.pessoaFisica?.pagamentos || 0);
    const resultado = receitas - despesas;

    return { receitas, despesas, resultado, controleInterno };
  };

  const handleSaveSettings = (novoNumColunas, novosCardsVisiveis) => {
    setNumColunas(novoNumColunas);
    setCardsVisiveis(novosCardsVisiveis);
    localStorage.setItem('dashboardNumColunas', novoNumColunas);
    localStorage.setItem('dashboardCardsVisiveis', JSON.stringify(novosCardsVisiveis));
  };

  const { receitas, despesas, resultado, controleInterno } = calcularValores();

  const cardAccentColors = {
    resultado: '#22C55E',
    impostos: '#F97316',
    fluxo: '#FB923C',
    despesas: '#F59E0B',
    dre: '#EA580C',
    lancamentos: '#FDBA74',
  };

  // Definição dos cards com configuração
  const cardsConfig = {
    resultado: {
      titulo: 'Resultado do Mês',
      visivel: cardsVisiveis.resultado,
      expandido: cardsExpandidos.resultado,
      renderConteudo: () => (
        <>
          <GraficoBarras receitas={receitas} despesas={despesas} />
          <Box sx={{ mt: 2.5, p: 2, textAlign: 'center', backgroundColor: isDarkMode ? 'rgba(249,115,22,0.12)' : '#FFF7ED', borderRadius: 1, border: '1px solid rgba(249,115,22,0.18)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Resultado Líquido
            </Typography>
            <Typography variant="h4" sx={{ fontSize: '2em', fontWeight: 700, color: resultado >= 0 ? '#00B389' : '#FF4873' }}>
              {formatarMoeda(resultado)}
            </Typography>
            {controleInterno > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Inclui {formatarMoeda(controleInterno)} de controle interno (não entra no imposto)
              </Typography>
            )}
          </Box>
        </>
      )
    },
    impostos: {
      titulo: 'Impostos a Pagar',
      visivel: cardsVisiveis.impostos,
      expandido: cardsExpandidos.impostos,
      renderConteudo: () => (
        <>
          {/* Se PF, mostra só PF */}
          {user?.tipoPessoa === 'PF' && (
            <>
              <Typography variant="subtitle1" sx={{ fontSize: '0.8em', mt: 0, mb: 1.5, color: '#EA580C', fontWeight: 700, background: 'rgba(249,115,22,0.09)', px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Carnê-Leão (PF)
              </Typography>
              <List sx={{ p: 0 }}>
                <DataListItem
                  label="Rendimentos (PF)"
                  value={formatarMoeda(metricas?.pessoaFisica?.rendimentos)}
                  valueColor="#00B389"
                  valueWeight={700}
                />
                <DataListItem
                  label="Despesas Dedutíveis"
                  value={formatarMoeda(metricas?.pessoaFisica?.pagamentos)}
                />
                <DataListItem
                  label="DNZ (Despesa p/ Zerar DARF)"
                  value={formatarMoeda(
                    (() => {
                      const rendimentos = parseFloat(metricas?.pessoaFisica?.rendimentos || 0);
                      const despesas = parseFloat(metricas?.pessoaFisica?.pagamentos || 0);
                      const darf = parseFloat(metricas?.pessoaFisica?.darf || 0);
                      const dnzBackend = parseFloat(metricas?.pessoaFisica?.dnz || 0);
                      if (darf <= 0) return 0;
                      if (rendimentos <= 0) return 0;
                      return dnzBackend;
                    })()
                  )}
                />
                <DataListItem
                  label="DARF a Pagar"
                  value={formatarMoeda(metricas?.pessoaFisica?.darf)}
                  showBorder
                  valueColor="#FF4873"
                  valueWeight={700}
                />
              </List>
            </>
          )}
          {/* Se PJ, mostra só PJ */}
          {user?.tipoPessoa === 'PJ' && (
            <>
              {renderCardImpostoPJ(metricas?.pessoaJuridica)}
            </>
          )}
          {/* Se híbrido, mostra os dois */}
          {user?.tipoPessoa === 'HIBRIDO' && (
            <>
              <Typography variant="subtitle1" sx={{ fontSize: '0.8em', mt: 0, mb: 1.5, color: '#EA580C', fontWeight: 700, background: 'rgba(249,115,22,0.09)', px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Carnê-Leão (PF)
              </Typography>
              <List sx={{ p: 0 }}>
                <DataListItem
                  label="Rendimentos (PF)"
                  value={formatarMoeda(metricas?.pessoaFisica?.rendimentos)}
                  valueColor="#00B389"
                  valueWeight={700}
                />
                <DataListItem
                  label="Despesas Dedutíveis"
                  value={formatarMoeda(metricas?.pessoaFisica?.pagamentos)}
                />
                <DataListItem
                  label="DNZ (Despesa p/ Zerar DARF)"
                  value={formatarMoeda(
                    (() => {
                      const rendimentos = parseFloat(metricas?.pessoaFisica?.rendimentos || 0);
                      const despesas = parseFloat(metricas?.pessoaFisica?.pagamentos || 0);
                      const darf = parseFloat(metricas?.pessoaFisica?.darf || 0);
                      const dnzBackend = parseFloat(metricas?.pessoaFisica?.dnz || 0);
                      if (darf <= 0) return 0;
                      if (rendimentos <= 0) return 0;
                      return dnzBackend;
                    })()
                  )}
                />
                <DataListItem
                  label="DARF a Pagar"
                  value={formatarMoeda(metricas?.pessoaFisica?.darf)}
                  showBorder
                  valueColor="#FF4873"
                  valueWeight={700}
                />
              </List>
              {/* Linha separadora visual entre PF e PJ */}
              <ListItem sx={{ borderTop: '2px solid', borderColor: 'divider', my: 2, px: 0 }} />
              {renderCardImpostoPJ(metricas?.pessoaJuridica)}
            </>
          )}
        </>
      )
    },
    fluxo: {
      titulo: 'Fluxo de Caixa (30 dias)',
      visivel: cardsVisiveis.fluxo,
      expandido: cardsExpandidos.fluxo,
      renderConteudo: () => {
        let dadosGrafico = [];
        if (dadosFluxo && dadosFluxo.faturamentos && dadosFluxo.despesas) {
          // Junta por mês, assumindo que os arrays têm os mesmos meses
          const meses = Array.from(new Set([
            ...dadosFluxo.faturamentos.map(f => f.mes),
            ...dadosFluxo.despesas.map(d => d.mes)
          ]));
          dadosGrafico = meses.map(mes => {
            const fat = dadosFluxo.faturamentos.find(f => f.mes === mes);
            const desp = dadosFluxo.despesas.find(d => d.mes === mes);
            return {
              dia: new Date(mes).toLocaleDateString('pt-BR', { month: '2-digit', year: '2-digit' }),
              saldo: (fat ? Number(fat.total) : 0) - (desp ? Number(desp.total) : 0)
            };
          });
        }
        // ...
        return (
          <>
            <GraficoLinha data={dadosGrafico} />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block' }}>
              Projeção de saldo futuro baseada em lançamentos e contas a pagar/receber.
            </Typography>
          </>
        );
      }
    },
    despesas: {
      titulo: 'Despesas por Categoria',
      visivel: cardsVisiveis.despesas,
      expandido: cardsExpandidos.despesas,
      renderConteudo: () => {
        // Exibe os nomes reais das despesas individuais vindas do backend
        const despesas = metricas?.despesasIndividuais || [];
        const totalDespesas = despesas.reduce((acc, despesa) => acc + parseFloat(despesa.value || 0), 0);
        const despesasComPercentual = despesas.map(despesa => ({
          ...despesa,
          percent: totalDespesas > 0 ? Math.round((parseFloat(despesa.value || 0) / totalDespesas) * 100) : 0
        }));
        const cores = ['#007bff', '#ffc107', '#FF4873', '#6c757d', '#00B389', '#8e44ad', '#e67e22'];
        return (
          <>
            <GraficoRosca
              data={despesasComPercentual}
              colors={cores}
              innerRadius={40}
              outerRadius={90}
            />
            <List sx={{ p: 0, mt: 2 }}>
              {despesasComPercentual.map((despesa, idx) => (
                <DataListItem
                  key={despesa.name}
                  label={`${despesa.name} (${despesa.percent}%)`}
                  value={`-R$ ${parseFloat(despesa.value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                />
              ))}
              <DataListItem
                label="Total"
                value={`-R$ ${totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                showBorder
                valueColor="#FF4873"
                valueWeight={700}
              />
            </List>
          </>
        );
      }
      // ...
    },
     lancamentos: {
      titulo: 'Últimos Lançamentos',
      visivel: cardsVisiveis.lancamentos,
      expandido: cardsExpandidos.lancamentos,
      renderConteudo: () => (
        <>
          <List sx={{ p: 0, fontSize: '0.9em' }}>
            {lancamentos.length === 0 && (
              <ListItem>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>Nenhum lançamento encontrado para o mês.</Typography>
              </ListItem>
            )}
            {lancamentos.map((lanc) => (
              <ListItem key={lanc.id || lanc._id || lanc.data + lanc.descricao}
                sx={{ display: 'flex', justifyContent: 'space-between', py: 1, px: 0, borderBottom: '1px dotted #dee2e6' }}>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  {new Date(lanc.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - {lanc.descricao}
                </Typography>
                <Typography variant="body2" sx={{ color: lanc.valor > 0 ? '#00B389' : '#FF4873', fontWeight: 500 }}>
                  {lanc.valor > 0 ? '+' : '-'}R$ {Math.abs(lanc.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
              </ListItem>
            ))}
          </List>
        </>
      )
    },
    dre: {
      titulo: 'DRE Simplificado',
      visivel: cardsVisiveis.dre,
      expandido: cardsExpandidos.dre,
      renderConteudo: () => {
        // Cálculo dos campos usando dados reais do backend
        const receita = parseFloat(metricas?.pessoaFisica?.rendimentos || 0) + parseFloat(metricas?.pessoaJuridica?.faturamento || 0);
        const impostosDireto = parseFloat(metricas?.pessoaFisica?.darf || 0) + parseFloat(metricas?.pessoaJuridica?.das || 0);
        const custos = parseFloat(metricas?.pessoaFisica?.pagamentos || 0);
        // Impostos sobre lucro: só para PF ou PJ fora do Simples Nacional
        let impostosLucro = 0;
        if (user?.tipoPessoa === 'PF') {
          impostosLucro = parseFloat(metricas?.pessoaFisica?.impostoRenda || 0);
        } else if (user?.tipoPessoa === 'PJ' && metricas?.pessoaJuridica?.regime !== 'Simples Nacional') {
          impostosLucro = parseFloat(metricas?.pessoaJuridica?.impostoRenda || 0);
        }
        const margemBruta = receita - impostosDireto - custos;
        const margemLiquida = margemBruta - impostosLucro;
        const margemLiquidaPercent = receita > 0 ? (margemLiquida / receita) * 100 : 0;
        return (
          <>
            <List sx={{ p: 0, mt: 1 }}>
              <DataListItem label="Receitas (vendas)" value={formatarMoeda(receita)} valueColor="#00B389" valueWeight={700} />
              <DataListItem label="Impostos direto (DARF/DAS)" value={formatarMoeda(impostosDireto)} valueColor="#FF4873" />
              <DataListItem label="Custos" value={formatarMoeda(custos)} valueColor="#6c757d" />
              <ListItem sx={{ borderTop: '2px solid', borderColor: 'divider', my: 1, px: 0 }} />
              <DataListItem label="Margem bruta" value={formatarMoeda(margemBruta)} valueColor="#007bff" valueWeight={600} showBorder />
              {((user?.tipoPessoa === 'PF') || (user?.tipoPessoa === 'PJ' && metricas?.pessoaJuridica?.regime !== 'Simples Nacional')) && (
                <DataListItem label="Impostos sobre lucro" value={formatarMoeda(impostosLucro)} valueColor="#FF4873" />
              )}
              <DataListItem label="Margem líquida" value={`${formatarMoeda(margemLiquida)} (${margemLiquidaPercent.toFixed(2)}%)`} valueColor={margemLiquida >= 0 ? '#00B389' : '#FF4873'} valueWeight={700} showBorder />
            </List>
          </>
        );
      }
    },

   
  };

  if (loading) {
    return (
      <>
        <Navbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ ml: 0, mt: '50px' }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Navbar e Sidebar */}
      <Navbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onSettingsOpen={() => setSettingsModalOpen(true)}
      />

      {/* Menu Lateral Flutuante (Inspirado no Meu Dinheiro) */}
      <DashboardSidebar
        mesAtual={mesAtual}
        anoAtual={anoAtual}
        onMesChange={handleMesChange}
        onConfigOpen={() => setSettingsModalOpen(true)}
      />

      {/* Modal de Configurações */}
      <SettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        numColunas={numColunas}
        cardsVisiveis={cardsVisiveis}
        onSave={handleSaveSettings}
      />

      {/* Conteúdo Principal */}
      <Box
        sx={{
          flexGrow: 1,
          padding: '70px 30px 30px 30px',
          paddingLeft: { xs: '30px', md: '110px' },
          backgroundColor: isDarkMode ? 'background.default' : '#FFFAF5',
          minHeight: '100vh',
        }}
      >
        {/* Cabeçalho da página */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300, fontSize: '1.6em', borderLeft: '4px solid #F97316', paddingLeft: '12px' }}>
              Visão Geral
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
            Período: <strong>{meses[mesAtual - 1]}/{anoAtual}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {metricas && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[...Array(colunasEfetivas)].map((_, colIdx) => (
              <Box key={colIdx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                {ordemCards
                  .map((cardId, globalIdx) => {
                    if (globalIdx % colunasEfetivas !== colIdx) return null;
                    const cardConfig = cardsConfig[cardId];
                    if (!cardConfig || !cardConfig.visivel) return null;
                    return (
                      <Card
                        key={cardId}
                        sx={{
                          transition: 'all 0.2s ease',
                          width: '100%',
                          mb: 0,
                          borderTop: `3px solid ${cardAccentColors[cardId] || '#F97316'}`,
                          overflow: 'hidden',
                          '&:hover': { boxShadow: '0 4px 20px rgba(249,115,22,0.18)' },
                        }}
                        draggable
                        onDragStart={() => handleDragStart(globalIdx)}
                        onDragOver={(e) => handleDragOver(e, globalIdx)}
                        onDragEnd={handleDragEnd}
                      >
                        <CardContent sx={{ pb: cardConfig.expandido ? 2 : '16px !important' }}>
                          <Box
                            className="card-header"
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: cardConfig.expandido ? 2 : 0,
                              borderBottom: '1px solid',
                              borderColor: isDarkMode ? 'rgba(249,115,22,0.25)' : 'rgba(249,115,22,0.18)',
                              pb: 1.5,
                              mx: -2,
                              px: 2,
                              pt: 1.5,
                              mt: -2,
                              background: isDarkMode ? 'rgba(249,115,22,0.07)' : 'rgba(249,115,22,0.04)',
                              '&:hover .icon-button': { opacity: 1 },
                              '&:hover .drag-icon': { opacity: 1 },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DragIndicatorIcon 
                                className="drag-icon"
                                sx={{ color: 'text.disabled', opacity: 0, transition: 'opacity 0.2s ease', fontSize: '1.2em', cursor: 'pointer' }} 
                              />
                              <Typography variant="h6" sx={{ fontSize: '1.1em', fontWeight: 600 }}>
                                {cardConfig.titulo}
                              </Typography>
                            </Box>
                            <IconButton 
                              className="icon-button"
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCardExpansao(cardId);
                              }}
                              sx={{ color: 'text.secondary', opacity: 0, transition: 'opacity 0.2s ease' }}
                            >
                              {cardConfig.expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                          <Collapse in={cardConfig.expandido} timeout="auto" unmountOnExit>
                            {cardConfig.renderConteudo()}
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default Dashboard;

